import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import ScrollToTop from 'shared/utils/scrollToTop';
import { PressureVesselDetails } from './PressureVesselDetails';
import { Button, SectionPage, H3, ErrorSummary } from '@snsw/react-component-library';
import styles from '../PlantItemDetails.module.scss';

import { BoilerDetails } from './BoilerDetails';
import { BuildingMaintenanceUnitDetails } from './BuildingMaintenanceUnitDetails';
import { ConcretePlacingUnitDetails } from './ConcretePlacingUnitDetails';
import { TowerCraneDetails } from './TowerCraneDetails';
import { MobileCraneDetails } from './MobileCraneDetails';
import { AmusementDeviceDetails } from './AmusementDeviceDetails';
import { EscalatorDetails } from './EscalatorDetails';
import { LiftDetails } from './LiftDetails';
import { RefData } from 'plantItemNew/constants/refData';
import { PagePath } from 'shared/constants/pagePath';
import { saveApplication } from 'shared/redux/applicationSlice';
import { storeRelatedDetails } from 'plantItemNew/redux/actions';
import { TopPageSummary } from 'shared/components/topPageSummary/TopPageSummary';
import { SaveOrCancelItem } from '../SaveOrCancelItem';

export const RelatedItemDetails = (props) => {
  ScrollToTop();

  const { index } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const savedPlantItems = useSelector((state) => state.plantItems);
  const application = useSelector((state) => state.application);
  const refData = useSelector((state) => state.referenceData.data);

  const [currentItemObject, setCurrentItemObject] = useState();
  const [relatedItemDetails, setRelatedItemDetails] = useState(null);
  const [errorSummary, setErrorSummary] = useState([]);

  useEffect(() => {
    if (Number.isInteger(+index) && savedPlantItems.items[index]) {
      let itemObject = savedPlantItems.items[index];

      //prevent user to access the page if plant item details and associated design are not completed
      if (!itemObject?.itemDetails?.isCompleted || !itemObject?.designDetails?.isCompleted) navigate.goBack();

      setCurrentItemObject(itemObject);
    }
  }, [savedPlantItems, index]);

  const componentRef = React.useRef();

  const renderRelatedItemDetails = () => {
    switch (currentItemObject?.itemDetails?.itemType) {
      case RefData.plantItemType.boiler:
        return (
          <BoilerDetails
            ref={componentRef}
            onChange={setRelatedItemDetails}
            value={currentItemObject?.relatedDetails}
            refData={refData}
            onErrorChange={setErrorSummary}
          />
        );

      case RefData.plantItemType.pressureVessel:
        return (
          <PressureVesselDetails
            ref={componentRef}
            onChange={setRelatedItemDetails}
            value={currentItemObject?.relatedDetails}
            refData={refData}
            onErrorChange={setErrorSummary}
          />
        );

      case RefData.plantItemType.lift:
        return (
          <LiftDetails
            ref={componentRef}
            onChange={setRelatedItemDetails}
            value={currentItemObject?.relatedDetails}
            refData={refData}
            onErrorChange={setErrorSummary}
          />
        );

      case RefData.plantItemType.escalator:
      case RefData.plantItemType.movingWalkway:
        return (
          <EscalatorDetails
            ref={componentRef}
            onChange={setRelatedItemDetails}
            value={currentItemObject?.relatedDetails}
            refData={refData}
            onErrorChange={setErrorSummary}
          />
        );

      case RefData.plantItemType.amusementDevice:
        return (
          <AmusementDeviceDetails
            ref={componentRef}
            onChange={setRelatedItemDetails}
            value={currentItemObject?.relatedDetails}
            applicationId={application?.applicationId}
            refData={refData}
            onErrorChange={setErrorSummary}
          />
        );

      case RefData.plantItemType.mobileCrane:
        return (
          <MobileCraneDetails
            ref={componentRef}
            onChange={setRelatedItemDetails}
            value={currentItemObject?.relatedDetails}
            refData={refData}
            onErrorChange={setErrorSummary}
          />
        );

      case RefData.plantItemType.towerCrane:
        return (
          <TowerCraneDetails
            ref={componentRef}
            onChange={setRelatedItemDetails}
            value={currentItemObject?.relatedDetails}
            applicationId={application?.applicationId}
            refData={refData}
            onErrorChange={setErrorSummary}
          />
        );

      case RefData.plantItemType.buildingMaintenanceUnit:
        return (
          <BuildingMaintenanceUnitDetails
            ref={componentRef}
            onChange={setRelatedItemDetails}
            value={currentItemObject?.relatedDetails}
            refData={refData}
            onErrorChange={setErrorSummary}
          />
        );

      case RefData.plantItemType.concretePlacingBoom:
        return (
          <ConcretePlacingUnitDetails
            ref={componentRef}
            onChange={setRelatedItemDetails}
            value={currentItemObject?.relatedDetails}
            refData={refData}
            onErrorChange={setErrorSummary}
          />
        );

      default:
        return null;
    }
  };

  const getItemObject = () => {
    let componentValid = componentRef.current.validate();

    let relatedDetails = {
      isCompleted: componentValid,
      ...relatedItemDetails,
    };

    return relatedDetails;
  };

  const savePlantItemDetails = (relatedDetails, index) => {
    dispatch(storeRelatedDetails(relatedDetails, index));
    dispatch(saveApplication(true));
  };

  const onSaveClick = () => {
    let relatedDetails = getItemObject();
    savePlantItemDetails(relatedDetails, index);
    navigate(PagePath.plantItemList);
  };

  const onFinaliseClick = () => {
    let relatedDetails = getItemObject();
    if (relatedDetails.isCompleted) {
      savePlantItemDetails(relatedDetails, index);
      navigate(PagePath.plantItemList);
    }
  };

  const summmaryTitlePropertyMap = [
    {
      title: 'Plant item details',
      itemName: 'itemDetails',
      link: `${PagePath.plantDetails}/${index}`,
    },
    {
      title: 'Associated design registration',
      itemName: 'designDetails',
      link: `${PagePath.associatedDesign}/${index}`,
    },
  ];

  const detailLabelPropertyMap = [
    { title: 'Plant item:', itemPropertyName: 'itemTypeText' },
    { title: 'Type:', itemPropertyName: 'subType' },
    { title: 'Manufacturer:', itemPropertyName: 'manufacturer' },
    { title: 'Serial number:', itemPropertyName: 'serialNumber' },
    { title: 'Manufacturers model name:', itemPropertyName: 'modelNameOrNumber' },
    { title: 'Year of manufacture:', itemPropertyName: 'yearOfManufacture' },
    { title: 'Specific location of the item:', itemPropertyName: 'specificLocation' },
    { title: 'Unique identifier:', itemPropertyName: 'uniqueIdentifier' },
    { title: 'State design registered:', itemPropertyName: 'state' },
    { title: 'Design registration number:', itemPropertyName: 'registrationNumber' },
  ];

  return (
    <>
      <SectionPage>
        <TopPageSummary
          titlePropertyMap={summmaryTitlePropertyMap}
          detailPropertyMap={detailLabelPropertyMap}
          item={currentItemObject}
        />
      </SectionPage>
      <ErrorSummary errors={errorSummary} />
      <SectionPage>
        <H3>Related item details</H3>
        {renderRelatedItemDetails()}

        <Button className={styles.marginTopThreeHalfRem} onClick={onFinaliseClick}>
          Complete item addition
        </Button>
      </SectionPage>
      <SaveOrCancelItem onSaveClick={onSaveClick} index={index} />
    </>
  );
};
